"use client"

import { useState, useCallback, useEffect } from "react"
import Cropper from "react-easy-crop"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Label } from "@/components/ui/label"
import { Loader2, RotateCcw } from "@/lib/icons"
import { isLocalOrManagedImageUrl } from "@/lib/image-utils"
import { useTranslations } from "next-intl"

interface ImageCropperProps {
  open: boolean
  onClose: () => void
  imageSrc: string
  aspect: number
  onCropComplete: (croppedImageUrl: string) => void
  /** Overrides the default round-for-square-else-rect guess — pass this
   * when the crop is never going to be masked into a circle (e.g. a
   * carousel card), regardless of which aspect ratio is picked. */
  cropShape?: "round" | "rect"
  /** When provided, renders a row of aspect-ratio buttons above the
   * cropper (e.g. Landscape/Square/Portrait) instead of locking to a
   * single fixed `aspect`. Selecting one calls `onAspectChange`. */
  aspectOptions?: { label: string; value: number }[]
  onAspectChange?: (value: number) => void
}

function getCropperFetchUrl(imageSrc: string): string {
  if (!imageSrc) return ""
  if (isLocalOrManagedImageUrl(imageSrc)) return imageSrc
  if (typeof window !== "undefined" && imageSrc.startsWith(window.location.origin)) {
    return imageSrc
  }
  if (imageSrc.startsWith("http")) {
    return `/api/proxy/image?url=${encodeURIComponent(imageSrc)}`
  }
  return imageSrc
}

export function ImageCropper({ open, onClose, imageSrc, aspect, onCropComplete, cropShape, aspectOptions, onAspectChange }: ImageCropperProps) {
  const t = useTranslations("imageCropper")
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null)
  const [processing, setProcessing] = useState(false)
  const [brightness, setBrightness] = useState(100)
  const [contrast, setContrast] = useState(100)
  const [saturation, setSaturation] = useState(100)
  const resetAdjustments = () => {
    setBrightness(100)
    setContrast(100)
    setSaturation(100)
  }
  const colorFilter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`
  // Local blob URL that is guaranteed to work with canvas (no CORS issues)
  const [localBlobUrl, setLocalBlobUrl] = useState<string>("")
  const [loading, setLoading] = useState(false)

  // When imageSrc changes, fetch it as a blob and create a local URL
  // This completely sidesteps all CORS/tainted-canvas issues
  useEffect(() => {
    if (!open || !imageSrc) {
      setLocalBlobUrl("")
      return
    }

    // Reset position/zoom/adjustments every time the dialog opens so users start fresh
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    resetAdjustments()

    // Already a blob or data URL — use directly
    if (imageSrc.startsWith("blob:") || imageSrc.startsWith("data:")) {
      setLocalBlobUrl(imageSrc)
      return
    }

    // For any other URL (external or proxy), fetch as blob
    let cancelled = false
    setLoading(true)

    const fetchAsBlob = async () => {
      try {
        const fetchUrl = getCropperFetchUrl(imageSrc)

        const response = await fetch(fetchUrl)
        if (!response.ok) {
          // If proxy fails, try the relative proxy URL directly (in case imageSrc is already proxied)
          if (imageSrc.startsWith("/")) {
            const fallbackResponse = await fetch(imageSrc)
            if (fallbackResponse.ok) {
              const blob = await fallbackResponse.blob()
              if (!cancelled) {
                const url = URL.createObjectURL(blob)
                setLocalBlobUrl(url)
              }
              return
            }
          }
          // Last resort: use the raw URL — cropper will display it but canvas crop may fail
          // This at least shows the image for framing
          if (!cancelled) setLocalBlobUrl(imageSrc)
          return
        }
        const blob = await response.blob()

        if (!cancelled) {
          const url = URL.createObjectURL(blob)
          setLocalBlobUrl(url)
        }
      } catch (err) {
        console.error("[ImageCropper] Failed to load image:", err)
        // Fallback: use raw URL so at least the cropper displays something
        if (!cancelled) setLocalBlobUrl(imageSrc)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchAsBlob()

    return () => {
      cancelled = true
    }
  }, [imageSrc, open])

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (localBlobUrl && localBlobUrl.startsWith("blob:") && localBlobUrl !== imageSrc) {
        URL.revokeObjectURL(localBlobUrl)
      }
    }
  }, [localBlobUrl, imageSrc])

  const onCropCompleteCallback = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const getCroppedImg = async (pixelCrop: any): Promise<Blob> => {
    // Load the local blob URL into an Image — guaranteed no CORS issues
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error("Failed to load image for cropping"))
      img.src = localBlobUrl
    })

    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error(t("no2dContext"))

    const outputWidth = Math.max(1, Math.round(pixelCrop.width))
    const outputHeight = Math.max(1, Math.round(pixelCrop.height))

    canvas.width = outputWidth
    canvas.height = outputHeight

    ctx.clearRect(0, 0, outputWidth, outputHeight)
    // Brightness/contrast/saturate map directly onto ctx.filter, applied at
    // draw time — same string driving the live Cropper preview below, so
    // what's shown while editing matches what gets exported.
    ctx.filter = colorFilter

    const sourceX = Math.max(0, pixelCrop.x)
    const sourceY = Math.max(0, pixelCrop.y)
    const sourceRight = Math.min(image.naturalWidth || image.width, pixelCrop.x + pixelCrop.width)
    const sourceBottom = Math.min(image.naturalHeight || image.height, pixelCrop.y + pixelCrop.height)
    const sourceWidth = Math.max(0, sourceRight - sourceX)
    const sourceHeight = Math.max(0, sourceBottom - sourceY)

    if (sourceWidth <= 0 || sourceHeight <= 0) {
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"))
            return
          }
          resolve(blob)
        }, "image/png")
      })
    }

    const destX = sourceX - pixelCrop.x
    const destY = sourceY - pixelCrop.y

    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      destX,
      destY,
      sourceWidth,
      sourceHeight
    )

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"))
          return
        }
        resolve(blob)
      }, "image/png")
    })
  }

  const handleSave = async () => {
    if (!croppedAreaPixels) return

    try {
      setProcessing(true)
      
      let blob: Blob
      try {
        blob = await getCroppedImg(croppedAreaPixels)
      } catch (canvasError: any) {
        // Canvas tainted — upload the full image and let server crop it
        // For now, just upload the original and use object-position CSS for framing
        console.warn("[ImageCropper] Canvas tainted, uploading original image with crop metadata")
        
        const imgResponse = await fetch(getCropperFetchUrl(imageSrc))
        if (!imgResponse.ok) {
          // If proxy also fails, just pass back the original URL as-is
          onCropComplete(imageSrc)
          onClose()
          return
        }
        blob = await imgResponse.blob()
      }

      // Upload the cropped image
      const file = new File([blob], "cropped-image.png", { type: blob.type || "image/png" })
      const formData = new FormData()
      formData.append("file", file)

      const uploadResponse = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      })

      const data = await uploadResponse.json()
      if (data.url) {
        onCropComplete(data.url)
        onClose()
      } else {
        throw new Error(data.error || "Upload failed")
      }
    } catch (error: any) {
      console.error("Error cropping image:", error)
      alert(`Failed to crop image: ${error.message || "Unknown error"}`)
    } finally {
      setProcessing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("cropZoomImage")}</DialogTitle>
        </DialogHeader>

        {aspectOptions && aspectOptions.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {aspectOptions.map((opt) => (
              <Button
                key={opt.label}
                type="button"
                size="sm"
                variant={aspect === opt.value ? "default" : "outline"}
                onClick={() => { setCrop({ x: 0, y: 0 }); setZoom(1); onAspectChange?.(opt.value) }}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        )}

        <div className="relative h-96 bg-gray-100 dark:bg-gray-900 rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : localBlobUrl ? (
            <Cropper
              image={localBlobUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              minZoom={0.1}
              // Square crops (avatars) render inside a circular mask everywhere in the app —
              // showing a round guide here instead of a square one means what you see while
              // cropping is what actually survives the circular clip, instead of finding out
              // after saving that the corners of a wide logo got cut off.
              cropShape={cropShape ?? (aspect === 1 ? "round" : "rect")}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropCompleteCallback}
              style={{ mediaStyle: { filter: colorFilter } }}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              {t("noImageAvailable")}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t("zoom")}</Label>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-muted-foreground gap-1"
                onClick={() => { setCrop({ x: 0, y: 0 }); setZoom(0.1) }}
              >
                <RotateCcw className="h-3 w-3" />
                {t("reset")}
              </Button>
            </div>
            <Slider
              value={[zoom]}
              onValueChange={(value) => setZoom(value[0])}
              min={0.1}
              max={3}
              step={0.01}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t("adjustments")}</Label>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-muted-foreground gap-1"
                onClick={resetAdjustments}
              >
                <RotateCcw className="h-3 w-3" />
                {t("reset")}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{t("brightness")}</span>
                  <span>{brightness}%</span>
                </div>
                <Slider value={[brightness]} onValueChange={(v) => setBrightness(v[0])} min={50} max={150} step={1} />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{t("contrast")}</span>
                  <span>{contrast}%</span>
                </div>
                <Slider value={[contrast]} onValueChange={(v) => setContrast(v[0])} min={50} max={150} step={1} />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{t("saturation")}</span>
                  <span>{saturation}%</span>
                </div>
                <Slider value={[saturation]} onValueChange={(v) => setSaturation(v[0])} min={0} max={200} step={1} />
              </div>
              {/* Sharpness was removed, not left broken. It never had a live
                  preview — canvas has no CSS-filter equivalent for sharpen,
                  so it only ran a manual 3x3 convolution at export time,
                  which is exactly why dragging the slider looked like
                  nothing happened. Giving it a real live preview would mean
                  re-running that O(width*height*9) pass on every slider
                  tick, which genuinely does jank on a full-resolution
                  image — not worth it for a feature this minor. */}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={processing}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSave} disabled={processing || loading || !localBlobUrl}>
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("processing")}
              </>
            ) : (
              "Save & Upload"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
